const API = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001/api';
const SESSION_KEY = 'admin_access_token';
let token: string | null = sessionStorage.getItem(SESSION_KEY);

let _navigateFn: ((path: string) => void) | null = null;
export function setNavigate(fn: (path: string) => void) { _navigateFn = fn; }

export function getToken(): string | null { return token; }
export function getApiBase(): string { return API; }
export function setToken(t: string) { token = t; sessionStorage.setItem(SESSION_KEY, t); }
export function setRefreshToken(_t: string) { /* refresh token managed via HttpOnly cookie */ }
export async function silentRefresh(): Promise<boolean> {
  try {
    const r = await fetch(`${API}/auth/refresh`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include' });
    if (!r.ok) return false;
    const data = await r.json() as { data: { access_token: string } };
    setToken(data.data.access_token);
    return true;
  } catch { return false; }
}
export function loadToken() { /* no-op: token loaded from sessionStorage at module init */ }
/** セッション切れをアプリ本体（useAuth）へ知らせるイベント名。ログイン画面へ戻し、ポーリングを止める */
export const AUTH_EXPIRED_EVENT = 'admin-auth-expired';
export function clearToken() {
  try { window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT)); } catch { /* SSR/テスト環境 */ }
  token = null;
  sessionStorage.removeItem(SESSION_KEY);
  localStorage.removeItem('admin_token'); localStorage.removeItem('admin_refresh_token'); localStorage.removeItem('admin_user');
}

/**
 * 会社サブドメインをホスト名から自動検出する
 *
 * 本番: yamato-kisen.catms.com → "yamato-kisen"
 * ローカル開発: localhost → VITE_COMPANY_SUBDOMAIN 環境変数を使用
 */
function getCompanySubdomain(): string {
  const hostname = window.location.hostname;
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    // 例: yamato-kisen.catms.com → ['yamato-kisen', 'catms', 'com']
    const parts = hostname.split('.');
    if (parts.length >= 3 && parts[0]) return parts[0];
  }
  // ローカル開発用フォールバック（.env.local で設定）
  return (import.meta.env.VITE_COMPANY_SUBDOMAIN as string | undefined) ?? 'unison-tech';
}

/**
 * この端末に固有のID（初回アクセス時に作って端末内に保存する）。
 * モバイル回線は接続ごとにIPが変わるため、IPだけでは「一度ログインできた端末」を
 * 判別できず、打ち間違いでログイン回数制限に引っかかってしまう。
 * サーバー側はこの値を実績キーとして扱う（船員アプリと同じ仕組み）。
 */
const CLIENT_ID_KEY = 'catms_client_device_id';
function getClientDeviceId(): string {
  try {
    let id = localStorage.getItem(CLIENT_ID_KEY);
    if (!id) {
      id = (crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);
      localStorage.setItem(CLIENT_ID_KEY, id);
    }
    return id;
  } catch { return ''; }
}

async function api<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const h: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Company-Subdomain': getCompanySubdomain(),
    'X-App-Type': 'admin',
    'X-Client-Device-Id': getClientDeviceId(),
    ...(opts.headers as Record<string, string> ?? {}),
  };
  if (token) h['Authorization'] = `Bearer ${token}`;
  // FormData（ファイル添付）は boundary をブラウザに任せる
  if (opts.body instanceof FormData) delete h['Content-Type'];
  const r = await fetch(`${API}${path}`, { ...opts, headers: h, credentials: 'include' });
  if (r.status === 401) {
    const refreshed = await silentRefresh();
    if (refreshed && token) {
      h['Authorization'] = `Bearer ${token}`;
      const retryRes = await fetch(`${API}${path}`, { ...opts, headers: h, credentials: 'include' });
      if (!retryRes.ok) { const e = await retryRes.json().catch(() => ({})); throw new Error(errMessage(e) ?? `HTTP ${retryRes.status}`); }
      return retryRes.json() as Promise<T>;
    }
    clearToken();
    if (_navigateFn) _navigateFn('/login');
    else window.location.replace('/login');
    throw new Error('認証が必要です。再ログインしてください。');
  }
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(errMessage(e) ?? `HTTP ${r.status}`); }
  return r.json();
}

/** サーバーのエラー応答から利用者向けの文言を取り出す（{ error: { message } } と { error: "文言" } の両方に対応） */
function errMessage(e: any): string | undefined {
  const err = e?.error;
  if (!err) return undefined;
  if (typeof err === 'string') return err;
  if (typeof err?.message === 'string') return err.message;
  return undefined;
}

export type LoginResult =
  | { mfa_required: true; mfa_token: string }
  | { data: { access_token: string; refresh_token: string; user: Record<string, unknown> } };

export async function login(id: string, pw: string): Promise<LoginResult> {
  const subdomain = getCompanySubdomain();
  const r = await fetch(`${API}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Company-Subdomain': subdomain,
      'X-App-Type': 'admin',
      'X-Client-Device-Id': getClientDeviceId(),
    },
    body: JSON.stringify({ employee_code: id, password: pw }),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({})) as { error?: { message?: string } };
    throw new Error(e.error?.message ?? `HTTP ${r.status}`);
  }
  const res = await r.json() as { data: { mfa_required?: boolean; mfa_token?: string; access_token?: string; refresh_token?: string; user?: Record<string, unknown> } };
  if (res.data.mfa_required) {
    return { mfa_required: true, mfa_token: res.data.mfa_token! };
  }
  if (res.data.refresh_token) setRefreshToken(res.data.refresh_token);
  return { data: { access_token: res.data.access_token!, refresh_token: res.data.refresh_token!, user: res.data.user! } };
}

export async function mfaChallenge(mfaToken: string, code: string, trustIp: boolean) {
  const subdomain = getCompanySubdomain();
  const r = await fetch(`${API}/auth/mfa/challenge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Company-Subdomain': subdomain,
      'X-App-Type': 'admin',
      'X-Client-Device-Id': getClientDeviceId(),
    },
    body: JSON.stringify({ mfa_token: mfaToken, code, trust_ip: trustIp }),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({})) as { error?: { message?: string } };
    throw new Error(e.error?.message ?? `HTTP ${r.status}`);
  }
  const res = await r.json() as { data: { access_token: string; refresh_token: string; user: Record<string, unknown> } };
  if (res.data.refresh_token) setRefreshToken(res.data.refresh_token);
  return res.data;
}

export async function mfaSetup() {
  return api<{ data: { secret: string; qrcode: string } }>('/auth/mfa/setup', { method: 'POST' }).then(r => r.data);
}

export async function mfaEnable(code: string) {
  return api<{ data: { message: string } }>('/auth/mfa/enable', { method: 'POST', body: JSON.stringify({ code }) }).then(r => r.data);
}

export async function mfaDisable(code: string) {
  return api<{ data: { message: string } }>('/auth/mfa/disable', { method: 'POST', body: JSON.stringify({ code }) }).then(r => r.data);
}

export function get<T>(p: string, q: Record<string, string> = {}) { return api<T>(`${p}?${new URLSearchParams(q)}`); }
export function postForm<T>(p: string, form: FormData) { return api<T>(p, { method: 'POST', body: form }); }
export function post<T>(p: string, b: unknown) { return api<T>(p, { method: 'POST', body: JSON.stringify(b) }); }
export function put<T>(p: string, b: unknown) { return api<T>(p, { method: 'PUT', body: JSON.stringify(b) }); }
export function patch<T>(p: string, b: unknown) { return api<T>(p, { method: 'PATCH', body: JSON.stringify(b) }); }
export function del<T>(p: string) { return api<T>(p, { method: 'DELETE' }); }